import { useEffect, useState } from "react";
import { DERIVED_RIDE_STATUSES } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { planShowRideDays, fetchUndatedClassRides, fetchUnscheduledRides, mergeDedupedRides } from "./showRideDayPlan";

// Aggregate the active show's rides into per-rider and per-horse "next ride"
// summaries (#558). Loads the show's classes once, then that show's rides,
// then the SERVER-derived status for every ride (#544 batch endpoint) so a
// scratched or fully-scored ride can never be reported as someone's "next"
// ride (#857 — terminal = FINAL + SCRATCHED; there is no WITHDRAWN/NO_SHOW,
// foxbox collapses those into SCRATCHED).
//
// #1963 — the ride fetch used to be one listRides call per class (1+N round
// trips). It now shares useShowRides's (#1906) day-chunked plan
// (showRideDayPlan.js): one GET /ride?showId=...&day=... call per DISTINCT
// day the show's classes fall on, plus one call per undated (emergency/
// manual-add, #386) class, not one per class. Unlike useShowRides this hook
// has nothing to paint until the full set is in — it computes a single
// "next ride" per rider/horse across every class — so every day is fetched
// in parallel rather than staggered behind a first-paint.
//
// #2040 — plus one more show-wide call for rides with no scheduledTime at
// all, which can sit inside an otherwise-dated class and so match neither a
// day nor undatedClassIds. See showRideDayPlan.js's fetchUnscheduledRides.
//
// Picks each entity's NEXT ride: the earliest upcoming scheduledTime among its
// non-terminal rides, falling back to the earliest scheduled non-terminal
// ride, then the lowest-numbered non-terminal ride when times are absent —
// every tier restricted to non-terminal rides, so an entity with nothing left
// to ride renders EMPTY (null) rather than resurrecting a finished one.
// Returns { scheduledTime, ring, className } keyed by riderId / horseId.
function pickNextRide(rides, classNameById) {
  if (!rides?.length) return null;
  const eligible = rides.filter(
    (r) =>
      r.derivedStatus !== DERIVED_RIDE_STATUSES.SCRATCHED &&
      r.derivedStatus !== DERIVED_RIDE_STATUSES.FINAL,
  );
  if (!eligible.length) return null;
  const parse = (r) => (r.scheduledTime ? Date.parse(r.scheduledTime) : NaN);
  const timed = eligible.filter((r) => !Number.isNaN(parse(r))).sort((a, b) => parse(a) - parse(b));
  const now = Date.now();
  const chosen =
    timed.find((r) => parse(r) >= now) ?? // next upcoming
    timed[0] ?? // else earliest scheduled (may be past)
    [...eligible].sort((a, b) => (a.rideNumber ?? Infinity) - (b.rideNumber ?? Infinity))[0];
  if (!chosen) return null;
  return {
    scheduledTime: chosen.scheduledTime ?? null,
    ring: chosen.ring ?? null,
    className: classNameById[chosen.classId] ?? null,
  };
}

export function useNextRides(showId) {
  const scoreApi = useScoreApi();
  const [state, setState] = useState({ nextByRider: {}, nextByHorse: {}, loading: false });

  useEffect(() => {
    if (!showId) {
      setState({ nextByRider: {}, nextByHorse: {}, loading: false });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true }));
      try {
        // #1909/#1963 — the show's per-show summary row (dayKeys) fetched
        // alongside the class list, mirroring useShowRides: one extra
        // GetItem, in parallel, not a second round trip. Falls back to null
        // (planShowRideDays then reduces the class list) on any failure.
        const [classes, showSummary] = await Promise.all([
          scoreApi.classes.listClasses(showId).then(unwrap),
          scoreApi.shows
            .getShow(showId)
            .then(unwrap)
            .then((s) => s?.summary ?? null)
            .catch(() => null),
        ]);
        const classNameById = Object.fromEntries(
          classes.map((c) => [c.id, c.className || c.classNumber || c.id]),
        );
        const { dayKeys, undatedClassIds } = planShowRideDays(classes, showSummary);

        let rawRides;
        if (dayKeys.length === 0) {
          // No dated classes at all (e.g. an all-manual or empty show) — a
          // single show-wide call already covers everything, including any
          // timeless rides (there's no day filter for #2040 to bypass here).
          rawRides = unwrap(await scoreApi.rides.listRides(undefined, showId));
        } else {
          // #2040 — a ride with no scheduledTime at all inside an otherwise
          // dated class matches no day filter and isn't in undatedClassIds
          // either (that only covers classes whose EVERY ride is timeless).
          // fetchUnscheduledRides closes that gap with one more show-wide
          // call (not per class); merged with mergeDedupedRides (a union
          // keyed on ride id) rather than relied on alone, since it's only
          // ORDINARILY a superset of what fetchUndatedClassRides also
          // returns, not guaranteed to be (see fetchUnscheduledRides' own
          // comment). Without this, the third "lowest-numbered non-terminal
          // ride when times are absent" tier in pickNextRide below could
          // never see such a ride.
          const [dayLists, undatedList, unscheduledList] = await Promise.all([
            Promise.all(
              dayKeys.map((day) =>
                scoreApi.rides.listRides(undefined, showId, day).then(unwrap).catch(() => []),
              ),
            ),
            fetchUndatedClassRides(scoreApi, undatedClassIds),
            fetchUnscheduledRides(scoreApi, showId),
          ]);
          rawRides = mergeDedupedRides(dayLists.flat(), undatedList, unscheduledList);
        }

        // #857 — resolve each ride's SERVER-derived status (#544 batch
        // endpoint) so pickNextRide can exclude FINAL/SCRATCHED; the raw
        // Ride row's stored `status` never carries FINAL (that's derived from
        // scoring progress, not stored).
        const rideIds = [...new Set(rawRides.map((r) => r.id).filter(Boolean))];
        const statusRows = rideIds.length
          ? unwrap(await scoreApi.rides.batchRideStatus({ rideIds }))
          : [];
        const statusById = Object.fromEntries(statusRows.map((row) => [row.rideId, row.status]));
        const rides = rawRides.map((r) => ({ ...r, derivedStatus: statusById[r.id] ?? null }));

        const byRider = {};
        const byHorse = {};
        for (const r of rides) {
          if (r.riderId) (byRider[r.riderId] ??= []).push(r);
          if (r.horseId) (byHorse[r.horseId] ??= []).push(r);
        }
        const mapNext = (grouped) =>
          Object.fromEntries(
            Object.entries(grouped).map(([id, list]) => [id, pickNextRide(list, classNameById)]),
          );

        if (!cancelled) {
          setState({ nextByRider: mapNext(byRider), nextByHorse: mapNext(byHorse), loading: false });
        }
      } catch {
        if (!cancelled) setState({ nextByRider: {}, nextByHorse: {}, loading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showId, scoreApi]);

  return state;
}

export default useNextRides;
